import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Calendar, Users, ShieldAlert } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { useAuth } from '@/contexts/AuthContext';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { useFastNavigation } from '@/hooks/useFastNavigation';

export default function BookingChoice() {
  const navigate = useNavigate();
  const { fastNavigate, prefetch, pendingPath } = useFastNavigation();
  const { t } = useTranslation('crisis');
  const { activeRole, user } = useAuth();

  const isPractitioner = activeRole === 'practitioner';

  const handleChoice = (choice: 'crisis' | 'regular') => {
    sessionStorage.setItem('booking_choice_made', 'true');
    if (choice === 'crisis') {
      fastNavigate('/crisis-resources');
    } else {
      fastNavigate('/service-selection');
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-grow pt-16">
        <div className="bg-warm-50 min-h-full">
          <div className="container mx-auto px-4 py-12 md:py-20">
            <div className="max-w-6xl mx-auto">
              <h1 className="text-3xl md:text-5xl font-bold text-center text-earth-800 mb-8">
                {t('choiceTitle')}
              </h1>

              {/* Practitioner warning */}
              {isPractitioner && (
                <div className="max-w-2xl mx-auto mb-8">
                  <Alert className="border-amber-300 bg-amber-50">
                    <ShieldAlert className="h-5 w-5 text-amber-600" />
                    <AlertTitle className="text-amber-800 font-semibold">
                      Practitioner Account Detected
                    </AlertTitle>
                    <AlertDescription className="text-amber-700 mt-1">
                      You are currently logged in as a <strong>practitioner</strong>. 
                      Booking appointments requires a client account. 
                      Please sign out and log in with a client account to book sessions, 
                      or browse practitioners and group events without booking.
                    </AlertDescription>
                    <div className="mt-3 flex gap-3">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate('/practitioner-dashboard')}
                        className="border-amber-300 text-amber-700 hover:bg-amber-100"
                      >
                        Go to Dashboard
                      </Button>
                    </div>
                  </Alert>
                </div>
              )}

              <div className="grid md:grid-cols-3 gap-6 md:gap-8">
                {/* Regular Booking Button */}
                <button 
                  onMouseEnter={() => prefetch('/service-selection')}
                  onFocus={() => prefetch('/service-selection')}
                  onClick={() => !isPractitioner && handleChoice('regular')} 
                  disabled={isPractitioner || pendingPath === '/service-selection'}
                  style={{ background: 'linear-gradient(135deg, hsl(225, 73%, 57%), hsl(231, 48%, 48%))' }}
                  className={`group relative overflow-hidden rounded-2xl p-8 md:p-12 text-white shadow-xl transition-all duration-300 min-h-[300px] flex flex-col items-center justify-center ${
                    isPractitioner ? 'opacity-50 cursor-not-allowed' : 'hover:shadow-2xl hover:scale-105'
                  }`}
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <div className="relative z-10 flex flex-col items-center text-center space-y-6">
                    <div className="bg-white/20 backdrop-blur-sm rounded-full p-6">
                      <Calendar className="h-16 w-16" />
                    </div>
                    <div>
                      <h2 className="text-2xl md:text-3xl font-bold mb-3">{t('regularButton')}</h2>
                      <p className="text-white/90 text-sm md:text-base">{t('regularDescription')}</p>
                    </div>
                  </div>
                </button>

                {/* Group Sessions Button - allow browsing even for practitioners */}
                <button
                  onMouseEnter={() => prefetch('/events')}
                  onFocus={() => prefetch('/events')}
                  onClick={() => fastNavigate('/events')}
                  disabled={pendingPath === '/events'}
                  style={{ background: 'linear-gradient(135deg, hsl(43, 96%, 56%), hsl(38, 92%, 50%))' }}
                  className="group relative overflow-hidden rounded-2xl p-8 md:p-12 text-warm-900 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 min-h-[300px] flex flex-col items-center justify-center disabled:opacity-70 disabled:cursor-wait"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <div className="relative z-10 flex flex-col items-center text-center space-y-6">
                    <div className="bg-warm-900/10 backdrop-blur-sm rounded-full p-6">
                      <Users className="h-16 w-16" />
                    </div>
                    <div>
                      <h2 className="text-2xl md:text-3xl font-bold mb-3">{t('groupButton')}</h2>
                      <p className="text-warm-900/80 text-sm md:text-base">{t('groupDescription')}</p>
                    </div>
                  </div>
                </button>

                {/* Crisis Button - always accessible */}
                <button
                  onMouseEnter={() => prefetch('/crisis-resources')}
                  onFocus={() => prefetch('/crisis-resources')}
                  onClick={() => handleChoice('crisis')}
                  disabled={pendingPath === '/crisis-resources'}
                  style={{ background: 'linear-gradient(135deg, hsl(351, 83%, 61%), hsl(347, 77%, 50%))' }}
                  className="group relative overflow-hidden rounded-2xl p-8 md:p-12 text-white shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 min-h-[300px] flex flex-col items-center justify-center disabled:opacity-70 disabled:cursor-wait"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <div className="relative z-10 flex flex-col items-center text-center space-y-6">
                    <div className="bg-white/20 backdrop-blur-sm rounded-full p-6">
                      <AlertTriangle className="h-16 w-16" />
                    </div>
                    <div>
                      <h2 className="text-2xl md:text-3xl font-bold mb-3">{t('crisisButton')}</h2>
                      <p className="text-white/90 text-sm md:text-base">{t('crisisDescription')}</p>
                    </div>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
